import { useState } from "react";

// US-076 / US-055 — reusable change-password form body.
//
// Pure presentational component shared by the forced first-login route
// (routes/change-password.tsx) and the self-service account-menu dialog
// (change-password-dialog.tsx). The parent owns the POST to
// /api/auth/change-password and surfaces any server error via the `error`
// prop (e.g. INVALID_CREDENTIALS → "Invalid credentials.", WEAK_PASSWORD).
//
// Client validation gates the submit button only — the server is the source
// of truth. Rules: current present, new password >= 8 chars, confirm matches.
// When `onCancel` is supplied a Cancel button is rendered (dialog use); the
// forced first-login flow omits it (the user must complete the change).

const PASSWORD_MIN_LENGTH = 8;

export interface ChangePasswordPayload {
  current_password: string;
  new_password: string;
}

interface ChangePasswordFormProps {
  onSubmit: (payload: ChangePasswordPayload) => void | Promise<void>;
  submitting: boolean;
  /** Server-side error (wrong current password, weak password, …). */
  error?: string;
  /** When provided, a Cancel button is rendered (self-service dialog). */
  onCancel?: () => void;
}

export function ChangePasswordForm({
  onSubmit,
  submitting,
  error,
  onCancel,
}: ChangePasswordFormProps) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");

  const passwordValid = next.length >= PASSWORD_MIN_LENGTH;
  const canSubmit = !submitting && current.length > 0 && passwordValid && confirm === next;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    void onSubmit({ current_password: current, new_password: next });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="space-y-1.5">
        <label htmlFor="cp-current" className="text-xs font-medium text-foreground">
          Current password
        </label>
        <input
          id="cp-current"
          type="password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          disabled={submitting}
          required
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/30 disabled:opacity-50"
        />
      </div>

      <div className="space-y-1.5">
        <label htmlFor="cp-new" className="text-xs font-medium text-foreground">
          New password
        </label>
        <input
          id="cp-new"
          type="password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          disabled={submitting}
          required
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/30 disabled:opacity-50"
        />
        <p className="text-xs text-muted-foreground">Use at least 8 characters.</p>
      </div>

      <div className="space-y-1.5">
        <label htmlFor="cp-confirm" className="text-xs font-medium text-foreground">
          Confirm new password
        </label>
        <input
          id="cp-confirm"
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          disabled={submitting}
          required
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/30 disabled:opacity-50"
        />
        {confirm.length > 0 && confirm !== next && (
          <p className="text-xs text-destructive">Passwords do not match.</p>
        )}
      </div>

      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}

      {onCancel ? (
        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="inline-flex items-center rounded-lg border border-border bg-background px-3.5 py-1.5 text-sm font-medium hover:bg-muted disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-primary px-3.5 py-1.5 text-sm font-medium text-primary-foreground shadow-glow disabled:opacity-50"
          >
            {submitting ? "Changing…" : "Change password"}
          </button>
        </div>
      ) : (
        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full inline-flex items-center justify-center rounded-lg bg-gradient-primary px-3.5 py-2 text-sm font-medium text-primary-foreground shadow-glow disabled:opacity-50"
        >
          {submitting ? "Changing…" : "Change password"}
        </button>
      )}
    </form>
  );
}
